import {
  DesktopFiltersDropdown,
  DesktopFiltersToolbar,
  DesktopFilterTag,
} from "features/filters/components";
import useAllLabels from "features/labels/datahooks/useAllLabels";

import Switch from "common/components/ui/Switch";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useWorkspaceAccounts from "common/datahooks/useWorkspaceAccounts";
import { typedObjectEntries } from "common/helpers/utils";
import useLeadsSearchParams from "common/hooks/useLeadsSearchParams";
import type { LeadsSearchParams, SelectedFacetParam } from "common/types";

import { LEADS_FILTERS } from "../../../constants";
import LeadsFilterOptions from "../LeadsFilterOptions";

interface DesktopFiltersProps {
  searchParams: LeadsSearchParams<SelectedFacetParam>;
  setSearchParams: (
    searchParams: Partial<LeadsSearchParams<SelectedFacetParam>>,
  ) => void;
  clearAllFilters: (() => void) | undefined;
}

export default function DesktopFilters({
  searchParams,
  setSearchParams,
  clearAllFilters,
}: DesktopFiltersProps) {
  const { id } = useSelectedWorkspaceContext();
  const { allLabels } = useAllLabels("workspace");
  const { workspaceAccounts } = useWorkspaceAccounts(id);
  const { hasFilters, setSearchParam } = useLeadsSearchParams(
    searchParams,
    setSearchParams,
  );

  return (
    <DesktopFiltersToolbar clearAllFilters={clearAllFilters}>
      {typedObjectEntries(searchParams).map(([key, selectedFilters]) => {
        if (
          key !== "keywords" &&
          (key === "favorite" ? selectedFilters : selectedFilters.length)
        ) {
          const { icon, label } = LEADS_FILTERS[key];

          const selectedFiltersLabels =
            key === "favorite"
              ? ["on"]
              : selectedFilters.map((selectedFacet) => {
                  if (key === "labels") {
                    //no label filter has a special id
                    if (selectedFacet.id === "NO_LABEL") {
                      return "No label";
                    }
                    if (allLabels) {
                      const label = allLabels.find(
                        ({ id }) => id === selectedFacet.id,
                      );
                      if (label) {
                        return label.name;
                      }
                      return "Missing label";
                    }
                    return "Loading...";
                  }
                  if (key === "lead_of") {
                    return workspaceAccounts
                      ? workspaceAccounts.find(
                          ({ id }) => id === selectedFacet.id,
                        ).full_name
                      : "Loading...";
                  }
                  return selectedFacet.name;
                });

          return (
            <DesktopFilterTag
              key={key}
              icon={icon}
              label={label}
              selectedFilters={selectedFiltersLabels}
              onRemove={() =>
                setSearchParams({
                  ...searchParams,
                  [key]: key === "favorite" ? false : [],
                })
              }
            >
              {key !== "favorite" && (
                <LeadsFilterOptions
                  searchParamsKey={key}
                  searchParams={searchParams}
                  setSearchParams={setSearchParams}
                />
              )}
            </DesktopFilterTag>
          );
        }
      })}

      <DesktopFiltersDropdown
        filters={LEADS_FILTERS}
        renderFilter={(filterKey) => {
          if (filterKey === "favorite") {
            return (
              <Switch
                size="sm"
                className="ml-auto"
                checked={searchParams.favorite}
                onCheckedChange={(isChecked) =>
                  setSearchParam("favorite", isChecked)
                }
              />
            );
          }
          return (
            <LeadsFilterOptions
              searchParamsKey={filterKey}
              searchParams={searchParams}
              setSearchParams={setSearchParams}
            />
          );
        }}
        hasFilters={hasFilters}
      />
    </DesktopFiltersToolbar>
  );
}
